import { AccountStore } from 'base/account_store';
import { IconClear, IconGift } from 'base/ui/icons';

import { AnalyticsService } from 'base/analytics';
import { navigateInternal } from 'base/navigation';
import { Button } from 'base/ui/button';
import { Inline } from 'base/ui/layout';
import { overlayStore } from 'base/ui/overlay';
import { TextSmall } from 'base/ui/typography';
import { authRouteToUrl } from 'blocks/auth/routes';
import { reaction } from 'mobx';
import React, { useEffect, useState } from 'react';
import styles from './referral_floatting_banner.css';

const KEY = 'DISMISSED_REFERRAL_GIFT_BANNER';
function hasBannerDismissed() {
  return localStorage.getItem(KEY) === 'true';
}

function dismissBanner() {
  localStorage.setItem(KEY, 'true');
}
export function installReferralGiftBanner({
  accountStore,
  analyticsService,
}: {
  accountStore: AccountStore;
  analyticsService: AnalyticsService;
}) {
  if (hasBannerDismissed()) return;

  reaction(
    () => accountStore.member?.activeReferralPromotion,
    (activeReferralPromotion) => {
      const onClose = () => {
        overlayStore.closeFloatingBanner();
        dismissBanner();
      };
      if (activeReferralPromotion && !overlayStore.currentFloatingBanner) {
        overlayStore.openFloatingBanner(
          <ReferralGiftBanner
            analyticsService={analyticsService}
            activeReferralPromotion={activeReferralPromotion}
            onClose={onClose}
          />,
        );
      }
    },
    { fireImmediately: true },
  );
}

const DAY_SECONDS = 86400;
const HOUR_SECONDS = 3600;
const ReferralGiftBanner = ({
  activeReferralPromotion,
  onClose,
  analyticsService,
}: {
  activeReferralPromotion: {
    discountPercent: number;
    expiredTime: number;
    referrerName: string;
  };
  analyticsService: AnalyticsService;
  onClose: () => void;
}) => {
  const { expiredTime, referrerName, discountPercent } =
    activeReferralPromotion;
  const [timeLeft, setTimeLeft] = useState<string>('');
  useEffect(() => {
    const updateCountdown = () => {
      const now = Date.now();
      const remainingMs = expiredTime - now;

      if (remainingMs <= 0) {
        setTimeLeft('Expired');
        return;
      }

      const totalSeconds = Math.floor(remainingMs / 1000);
      const days = Math.floor(totalSeconds / DAY_SECONDS); // 1 day = 86400 seconds

      if (days > 1) {
        setTimeLeft(`${days} day${days > 1 ? 's' : ''} left`);
      } else {
        const hours = Math.floor((totalSeconds % DAY_SECONDS) / HOUR_SECONDS);
        const minutes = Math.floor((totalSeconds % HOUR_SECONDS) / 60);
        const seconds = totalSeconds % 60;

        setTimeLeft(
          `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
        );
      }
    };

    updateCountdown();
    const interval = setInterval(() => {
      updateCountdown();
      // Clear countdown if more than 1 day
      if (expiredTime - Date.now() >= DAY_SECONDS * 1000) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiredTime]);

  return (
    <div className={styles.container}>
      <div className={styles.contentContainer}>
        <Inline spacing={4} yalign="center">
          <IconGift width={14} height={14} color="white" />
          <TextSmall color="white">
            {`${timeLeft} | ${referrerName} has gifted you ${discountPercent}% off on Starter or Pro plan.`}
          </TextSmall>
          <Button
            variant="subtle"
            onClick={(event) => {
              analyticsService.track({
                event: 'referral_flow:invitee_banner_clicked',
              });
              navigateInternal(event);
            }}
            href={authRouteToUrl({ path: 'upgrade', context: {} })}
          >
            <div className={styles.claimDiscountText}>
              <TextSmall>Claim discount</TextSmall>
            </div>
          </Button>
        </Inline>
      </div>
      <Button
        icon={IconClear}
        variant="subtle"
        size="small"
        onClick={onClose}
      />
    </div>
  );
};
